import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./verification.css";
import verification_mark from "../../assets/verification_mark.png";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { OtpInput } from "../../components/input/otpInput/OtpInput";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export default function Verification() {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(20);
  const [tick, setTick] = useState(false);
  const [resending, setResending] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const [status, setStatus] = useState("idle");
  const [otpKey, setOtpKey] = useState(0);
  const navigate = useNavigate();

  const email = localStorage.getItem("pendingEmail");

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);

    localStorage.setItem("verification_start_time", new Date().toISOString());
    localStorage.setItem("verification_timezone", timeZone);
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((t) => t - 1);
        setTick(true);
        setTimeout(() => setTick(false), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleOtpComplete = async (otpCode) => {
    setStatus("loading");
    const startTime = Date.now();
    try {
      const headers = buildHeaders();

      const res = await fetch(`${BASE_URL}/auth/verify-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email,
          code: otpCode,
          purpose: "email_verification",
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => ({}));

      const elapsed = Date.now() - startTime;
      const minLoading = 500;
      if (elapsed < minLoading) {
        await new Promise((resolve) => setTimeout(resolve, minLoading - elapsed));
      }

      if (!res.ok) {
        toast.error(data?.detail || t("auth.errorInvalidCode"));

        localStorage.setItem(
          "verification_failed_time",
          new Date().toISOString(),
        );
        setStatus("error");
     
        return;
      }

      setStatus("success");

      if (data?.access && data?.refresh) {
        localStorage.setItem("accessToken", data.access);
        localStorage.setItem("refreshToken", data.refresh);
      }

      localStorage.setItem(
        "verification_success_time",
        new Date().toISOString(),
      );
      localStorage.setItem("verification_attempts", "success");

      if (window.gtag) {
        window.gtag("event", "email_verified", {
          email_domain: email?.split("@")[1],
          verification_method: "email_code",
          timezone: userTimeZone,
        });
      }

      toast.success(t("auth.successVerification"));

      const userStatus = localStorage.getItem("user_status");
      if (userStatus === "worker") {
        localStorage.setItem("redirectAfterPassword", "/worker-invnit");
      } else {
        localStorage.setItem("redirectAfterPassword", "/account-type-select");
      }

      setTimeout(() => {
        navigate("/create-password");
      }, 2200);
    } catch (err) {
      console.error("❌ VERIFY ERROR:", err);
      const elapsed = Date.now() - startTime;
      const minLoading = 500;
      if (elapsed < minLoading) {
        await new Promise((resolve) => setTimeout(resolve, minLoading - elapsed));
      }
      toast.error(t("auth.errorVerificationFailed"));
      setStatus("error");

      if (window.gtag) {
        window.gtag("event", "verification_error", {
          error_type: "network",
        });
      }
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);

    try {
      const headers = buildHeaders();

      const res = await fetch(`${BASE_URL}/auth/resend-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email,
          purpose: "email_verification",
          timeZone: userTimeZone,
        }),
      });

      if (!res.ok) throw new Error();

      setTimeLeft(20);
      setStatus("idle");
      setOtpKey((k) => k + 1);

      localStorage.setItem(
        "verification_resend_time",
        new Date().toISOString(),
      );
      localStorage.setItem(
        "verification_resend_count",
        (
          parseInt(localStorage.getItem("verification_resend_count") || 0) + 1
        ).toString(),
      );

      toast.success(t("auth.successCodeResent"));

      if (window.gtag) {
        window.gtag("event", "code_resent", {
          email_domain: email?.split("@")[1],
        });
      }
    } catch {
      toast.error(t("auth.errorResendFailed"));
    } finally {
      setTimeout(() => setResending(false), 1000);
    }
  };

  return (
    <div className="verify">
      <main className="verify-main">
        <div className="verify-card">
          <OtpInput
            key={otpKey}
            length={6}
            status={status}
            onComplete={handleOtpComplete}
            onReset={() => setStatus("idle")}
            timeLeft={timeLeft}
            tick={tick}
            resending={resending}
            onResend={handleResend}
            resendLabel={t("auth.sendCodeAgain")}
            resendBtnLabel={t("auth.resendCode")}
            successMessage={t("auth.successVerification") || "Verification successfully"}
            avatarNode={<img src={verification_mark} alt="icon" />}
            title={t("auth.verificationCodeTitle")}
            subtitle={t("auth.verificationSubtitle", { email: email })}
          />
        </div>
      </main>

      <footer className="verify-footer">
        <span>{t("auth.copyright")}</span>
        <div>
          <a href="#">
            <IoMdLock /> {t("auth.privacy")}
          </a>
          <a href="#">
            <CiCircleQuestion /> {t("auth.getHelp")}
          </a>
        </div>
      </footer>
    </div>
  );
}
